import React, { useState, useEffect } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { useFetch } from "../../hooks/useFetch";
import { useAfterLogin } from "../../hooks/useAfterLogin";
import {
  API_EMAIL_LOGIN,
  API_EMAIL_REGISTER,
  API_EMAIL_RESET_PWD,
  API_EMAIL_SEND_CODE,
  API_GOOGLE_AUTH,
} from "../../constants/api";
import { useAtom } from "jotai";
import { AuthMode, userAtom } from "../../store/store";
import ToastService from "../../components/common/Toast/ToastService";
import Modal from "../common/Modal/Modal";
import Input from "../common/Input/Input";
import Button from "../common/Button/Button";
import "./AuthModal.css";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: AuthMode;
}

const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  initialMode = "login",
}) => {
  const [user, setUser] = useAtom(userAtom);
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState(""); // 验证码状态
  const [countdown, setCountdown] = useState(0); // 倒计时状态
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [verificationCodeError, setVerificationCodeError] = useState(""); // 验证码错误状态
  const [isGoogleLoading, setIsGoogleLoading] = useState(false); // 添加 Google 登录状态
  const [isEmailLoading, setIsEmailLoading] = useState(false); // 添加 email 登录状态
  const { request } = useFetch();
  const { afterLogin } = useAfterLogin();

  const googleLogin = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      try {
        setIsGoogleLoading(true); // 设置 loading 状态为 true

        const {
          data: { token, email },
        } = await request<{ token: string; email: string }>(API_GOOGLE_AUTH, {
          method: "POST",
          body: {
            code: codeResponse.code,
          },
        });
        localStorage.setItem("token", token);
        const nickname = email.slice(0, 3).toUpperCase();
        setUser({ ...user, email, isLogin: true, nickname });
        onClose();
        afterLogin();
        ToastService.success("Login successful");
      } catch (error) {
        const msg = error instanceof Error ? error.message : (error as string);
        ToastService.error(msg);
      } finally {
        setIsGoogleLoading(false); // 无论成功或失败，都将 loading 状态设置为 false
      }
    },
    onError(errorResponse) {
      ToastService.error("Operation failed");
      setIsGoogleLoading(false); // 错误时也要重置 loading 状态
    },
    flow: "auth-code",
  });

  // 处理倒计时逻辑
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // 获取验证码的处理函数
  const handleGetVerificationCode = async () => {
    if (countdown > 0) {
      return;
    }

    if (!email) {
      setEmailError("Please enter your email address");
      return;
    }

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    // 设置倒计时60秒
    setCountdown(60);

    try {
      await request(API_EMAIL_SEND_CODE, {
        method: "POST",
        body: {
          email,
          mode: mode === "register" ? 0 : 1,
        },
      });
      ToastService.success("Verification code sent to your email");
    } catch (error) {
      ToastService.error(
        error instanceof Error ? error.message : (error as string)
      );
    }
  };

  // 新增：处理重置密码
  const handleResetPassword = () => {
    setMode("reset-password");
    clearForm();
  };

  const clearForm = () => {
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setVerificationCode("");
    setEmailError("");
    setPasswordError("");
    setConfirmPasswordError("");
    setVerificationCodeError("");
  };

  // 新增：提交重置密码
  const handleResetPasswordSubmit = async () => {
    // 验证邮箱
    if (!email) {
      setEmailError("Please enter your email address");
      return;
    }

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    // 验证验证码
    if (!verificationCode) {
      setVerificationCodeError("Please enter the verification code");
      return;
    }

    if (verificationCode.length !== 6) {
      setVerificationCodeError("Verification code should be 6 digits");
      return;
    }

    // 验证新密码
    if (!password) {
      setPasswordError("Please enter a new password");
      return;
    }

    if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return;
    }

    // 验证确认密码
    if (!confirmPassword) {
      setConfirmPasswordError("Please confirm your new password");
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match");
      return;
    }

    try {
      await request(API_EMAIL_RESET_PWD, {
        method: "POST",
        body: {
          email,
          code: verificationCode,
          password,
        },
      });
      ToastService.success("Password reset successful");
      // 清空表单
      clearForm();
      setMode("login");
    } catch (error) {
      ToastService.error(
        error instanceof Error ? error.message : (error as string)
      );
    }
  };

  const handleSubmit = async () => {
    if (isEmailLoading) return;

    // 如果是重置密码模式，使用重置密码的处理函数
    if (mode === "reset-password") {
      handleResetPasswordSubmit();
      return;
    }

    // 重置错误信息
    setEmailError("");
    setPasswordError("");
    setConfirmPasswordError("");
    setVerificationCodeError("");

    // 验证邮箱
    if (!email) {
      setEmailError("Please enter your email address");
      return;
    }

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    // 验证密码
    if (!password) {
      setPasswordError("Please enter your password");
      return;
    }

    if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return;
    }

    // 如果是注册模式，验证确认密码和验证码
    if (mode === "register") {
      if (!confirmPassword) {
        setConfirmPasswordError("Please confirm your password");
        return;
      }

      if (password !== confirmPassword) {
        setConfirmPasswordError("Passwords do not match");
        return;
      }

      // 验证验证码
      if (!verificationCode) {
        setVerificationCodeError("Please enter the verification code");
        return;
      }

      if (verificationCode.length !== 6) {
        setVerificationCodeError("Verification code should be 6 digits");
        return;
      }
    }

    // 提交表单
    let api: string;
    let body: Record<string, string>;
    if (mode === "login") {
      api = API_EMAIL_LOGIN;
      body = {
        email,
        password,
      };
    } else {
      api = API_EMAIL_REGISTER;
      body = {
        email,
        code: verificationCode,
        password,
      };
    }

    try {
      setIsEmailLoading(true); // 设置 loading 状态为 true
      const {
        data: { token },
      } = await request<{ token: string; email: string }>(api, {
        method: "POST",
        body,
      });
      localStorage.setItem("token", token);
      const nickname = email.slice(0, 3).toUpperCase();
      setUser({ ...user, email, isLogin: true, nickname });
      onClose();
      afterLogin();
      ToastService.success("Login successful");
    } catch (error) {
      const msg = error instanceof Error ? error.message : (error as string);
      ToastService.error(msg);
    } finally {
      setIsEmailLoading(false);
    }
  };

  const switchMode = () => {
    setMode(mode === "login" ? "register" : "login");
    // 切换模式时清空表单
    clearForm();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnClickOutside={false}
      title={
        mode === "login"
          ? "Login"
          : mode === "register"
          ? "Register"
          : "Reset Password"
      }
      className="auth-modal"
    >
      <div className="auth-container">
        <form>
          <div className="auth-inputs">
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setEmailError("")}
              placeholder="Enter your email address"
              validationState={emailError ? "error" : "default"}
              errorMessage={emailError}
              required
            />

            {mode !== "reset-password" && (
              <Input
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setPasswordError("")}
                placeholder="Enter your password"
                validationState={passwordError ? "error" : "default"}
                errorMessage={passwordError}
                required
              />
            )}

            {/* 登录模式下添加重置密码链接 */}
            {mode === "login" && (
              <div className="forgot-password">
                <button type="button" onClick={handleResetPassword}>
                  Forgot password?
                </button>
              </div>
            )}

            {/* 重置密码模式下显示验证码和新密码输入框 */}
            {mode === "reset-password" && (
              <>
                <div className="verification-code-container">
                  <Input
                    label="Verification Code"
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    onFocus={() => setVerificationCodeError("")}
                    placeholder="Enter verification code"
                    validationState={
                      verificationCodeError ? "error" : "default"
                    }
                    errorMessage={verificationCodeError}
                    required
                    className="verification-code-input"
                  />
                  <Button
                    onClick={handleGetVerificationCode}
                    variant="secondary"
                    state={countdown > 0 ? "disabled" : "default"}
                    className="verification-code-button"
                  >
                    {countdown > 0 ? `Resend in ${countdown}s` : "Get Code"}
                  </Button>
                </div>

                <Input
                  label="New Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setPasswordError("")}
                  placeholder="Enter new password"
                  validationState={passwordError ? "error" : "default"}
                  errorMessage={passwordError}
                  required
                />

                <Input
                  label="Confirm New Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onFocus={() => setConfirmPasswordError("")}
                  placeholder="Confirm new password"
                  validationState={confirmPasswordError ? "error" : "default"}
                  errorMessage={confirmPasswordError}
                  required
                />
              </>
            )}

            {mode === "register" && (
              <>
                <Input
                  label="Confirm Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onFocus={() => setConfirmPasswordError("")}
                  placeholder="Confirm your password"
                  validationState={confirmPasswordError ? "error" : "default"}
                  errorMessage={confirmPasswordError}
                  required
                />

                <div className="verification-code-container">
                  <Input
                    label="Verification Code"
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    onFocus={() => setVerificationCodeError("")}
                    placeholder="Enter verification code"
                    validationState={
                      verificationCodeError ? "error" : "default"
                    }
                    errorMessage={verificationCodeError}
                    required
                    className="verification-code-input"
                  />
                  <Button
                    onClick={handleGetVerificationCode}
                    variant="secondary"
                    state={countdown > 0 ? "disabled" : "default"}
                    className="verification-code-button"
                  >
                    {countdown > 0 ? `Resend in ${countdown}s` : "Get Code"}
                  </Button>
                </div>
              </>
            )}
          </div>

          <div className="auth-actions">
            <Button
              type="button"
              variant="primary"
              fullWidth
              onClick={handleSubmit}
              state={isEmailLoading ? "loading" : "default"}
            >
              {mode === "login"
                ? "Login"
                : mode === "register"
                ? "Register"
                : "Reset Password"}
            </Button>
          </div>
        </form>

        {mode !== "reset-password" && (
          <>
            <div className="auth-divider">
              <span>or</span>
            </div>

            <div className="social-login">
              <Button
                variant="secondary"
                onClick={googleLogin}
                fullWidth
                className="google-login-btn"
                state={isGoogleLoading ? "loading" : "default"}
              >
                <div className="google-icon"></div>
                {mode === "login"
                  ? "Login with Google"
                  : "Register with Google"}
              </Button>
            </div>
          </>
        )}

        <div className="auth-switch">
          {mode === "login" ? (
            <p>
              Don't have an account?
              <button type="button" onClick={switchMode}>
                Register now
              </button>
            </p>
          ) : mode === "register" ? (
            <p>
              Already have an account?
              <button type="button" onClick={switchMode}>
                Login now
              </button>
            </p>
          ) : (
            <p>
              <button type="button" onClick={() => setMode("login")}>
                Back to login
              </button>
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AuthModal;
